import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../../../i18n/LanguageContext";
import { useAuth } from "../../../auth/AuthContext";
import { authClient } from "../../../auth/authClient";
import { dreamsClient } from "../../dreams/api/dreamsClient";
import { vaultEngine } from "../../dreams/utils/vaultEngine";
import { avatarClient } from "../api/avatarClient";
import { PwaInstallPanel } from "../../pwa/PwaInstallPanel";

const formatDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(lang === "en" ? "en-GB" : "pl-PL", { year: "numeric", month: "long", day: "numeric" });
  } catch (_) {
    return "—";
  }
};

const readPasswordChangeKey = () => {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash || "";
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) return "";
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  return params.get("pc") || params.get("password_change") || "";
};

const passwordChangeReturnUrl = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${window.location.pathname}${window.location.search}#/profil`;
};

const getProfileFallbackCopy = (isEn) => ({
  eyebrow: isEn ? "Your nook" : "Twój zakątek",
  titlePre: isEn ? "Welcome," : "Witaj,",
  accountTitle: isEn ? "Account" : "Konto",
  displayName: isEn ? "Handle" : "Ksywka",
  editDisplayName: isEn ? "Edit" : "Zmień",
  save: isEn ? "Save" : "Zapisz",
  cancel: isEn ? "Cancel" : "Anuluj",
  memberSince: isEn ? "Member since" : "Konto założone",
  dreamsCount: isEn ? "Saved dreams" : "Zapisanych snów",
  backToRoom: isEn ? "Room" : "Pokoik",
  myDreams: isEn ? "My dreams" : "Moje sny",
  logout: isEn ? "Sign out" : "Wyloguj się",
});

export const ProfilePage = () => {
  const { t, lang } = useLang();
  const { user, isAuthenticated, authReady, logout, updateProfile } = useAuth();
  const copy = t.profile || {};
  const isEn = lang === "en";
  const fallback = getProfileFallbackCopy(isEn);
  const profileCopy = {
    linkConfirmed: isEn ? "The email link has been confirmed. Enter your current and new Morpheus password." : "Link z maila został potwierdzony. Wpisz obecne i nowe hasło Morfeusza.",
    sendingLink: isEn ? "Sending confirmation link…" : "Wysyłam link potwierdzający…",
    passwordMailSent: isEn ? "We sent a password-change confirmation link to the email assigned to your account." : "Wysłaliśmy link potwierdzający zmianę hasła na e-mail przypisany do konta.",
    linkSendError: isEn ? "The confirmation link could not be sent." : "Nie udało się wysłać linku potwierdzającego.",
    confirmFirst: isEn ? "First confirm the password change with the email link." : "Najpierw potwierdź zmianę hasła linkiem z maila.",
    fillAll: isEn ? "Fill in all password-change fields." : "Uzupełnij wszystkie pola zmiany hasła.",
    mismatch: isEn ? "The new passwords do not match." : "Nowe hasła nie są takie same.",
    tooShort: isEn ? "The new password is too short." : "Nowe hasło jest za krótkie.",
    changingPassword: isEn ? "Changing the Morpheus password…" : "Zmieniam hasło Morfeusza…",
    passwordChanged: isEn ? "The Morpheus password has been changed. From now on, this new password opens your account and private dreams." : "Hasło Morfeusza zostało zmienione. Od teraz otworzysz konto i prywatne sny nowym hasłem.",
    passwordChangeError: isEn ? "The Morpheus password could not be changed." : "Nie udało się zmienić hasła Morfeusza.",
    deleteMailSent: isEn ? "We sent an account-deletion confirmation link. Check your email." : "Wysłaliśmy link potwierdzający usunięcie konta. Sprawdź e-mail.",
    security: isEn ? "Security" : "Bezpieczeństwo",
    changePassword: isEn ? "Change Morpheus password" : "Zmień hasło Morfeusza",
    passwordIntro: isEn ? "Your Morpheus password opens the account and private dreams. Changing it requires email confirmation and your current password. Without the current password, access to encrypted dreams cannot be transferred." : "Hasło Morfeusza otwiera konto i prywatne sny. Zmiana wymaga potwierdzenia e-mailem oraz obecnego hasła. Bez obecnego hasła nie da się przenieść dostępu do zaszyfrowanych snów.",
    sendMail: isEn ? "Send link by email" : "Wyślij link na e-mail",
    sending: isEn ? "Sending…" : "Wysyłam…",
    currentPassword: isEn ? "Current Morpheus password" : "Obecne hasło Morfeusza",
    newPassword: isEn ? "New Morpheus password" : "Nowe hasło Morfeusza",
    repeatPassword: isEn ? "Repeat new password" : "Powtórz nowe hasło",
    passwordHint: isEn ? "The email link confirms that you requested the change. The current password is still required because only it can transfer access to private dreams." : "Link z maila potwierdza, że to Ty prosisz o zmianę. Obecne hasło nadal jest potrzebne, bo tylko ono pozwala przenieść dostęp do prywatnych snów.",
    changing: isEn ? "Changing…" : "Zmieniam…",
    deleteTitle: isEn ? "Account deletion" : "Usunięcie konta",
    deleteAccount: isEn ? "Delete account" : "Usuń konto",
    deleteHint: isEn ? "Deleting the account will remove your dreams, settings and private vault. After email confirmation, the account will be blocked and a separate email will include an undo link valid for 12 hours." : "Usunięcie konta usunie Twoje sny, ustawienia i prywatny sejf. Po potwierdzeniu e-mailem konto zostanie zablokowane, a w osobnym mailu dostaniesz link cofnięcia ważny przez 12 godzin.",
    sendConfirm: isEn ? "Send confirmation link" : "Wyślij link potwierdzający",
    tierFallback: isEn ? "Package" : "Pakiet",
    avatarTitle: isEn ? "Avatar" : "Avatar",
    avatarHint: isEn ? "Choose an image up to 1 MB. It will replace your Morpheus profile avatar." : "Wybierz obraz do 1 MB. Zastąpi avatar profilu Morfeusza.",
    chooseAvatar: isEn ? "Choose file" : "Wybierz plik",
    uploadAvatar: isEn ? "Save avatar" : "Zapisz avatar",
    uploadingAvatar: isEn ? "Saving…" : "Zapisuję…",
    removeAvatar: isEn ? "Remove avatar" : "Usuń avatar",
    removingAvatar: isEn ? "Removing…" : "Usuwam…",
    avatarSaved: isEn ? "Avatar saved." : "Zapisano avatar.",
    avatarRemoved: isEn ? "Avatar removed." : "Usunięto avatar.",
    avatarFailed: isEn ? "Could not update avatar." : "Nie udało się zaktualizować avatara.",
    avatarLoadFailed: isEn ? "Could not load avatar." : "Nie udało się wczytać avatara.",
    avatarNoFile: isEn ? "Choose an image file first." : "Najpierw wybierz plik obrazu.",
    avatarTooLarge: isEn ? "Avatar may be up to 1 MB." : "Avatar może mieć maksymalnie 1 MB.",
  };
  const initialKey = useMemo(readPasswordChangeKey, []);
  const fileRef = useRef(null);

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [dreamsCount, setDreamsCount] = useState("—");
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState("");
  const [avatarStatus, setAvatarStatus] = useState("");
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [securityStep, setSecurityStep] = useState(initialKey ? "form" : "closed");
  const [mailKey, setMailKey] = useState(initialKey);
  const [mailBusy, setMailBusy] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [passwordStatus, setPasswordStatus] = useState(initialKey ? profileCopy.linkConfirmed : "");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [deleteStep, setDeleteStep] = useState("closed");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadDreamsCount = async () => {
      if (!authReady || !isAuthenticated) {
        if (mounted) setDreamsCount("—");
        return;
      }
      try {
        const response = await dreamsClient.list();
        if (!mounted) return;
        setDreamsCount(Array.isArray(response?.dreams) ? response.dreams.length : "—");
      } catch (_) {
        if (mounted) setDreamsCount("—");
      }
    };
    loadDreamsCount();
    window.addEventListener("focus", loadDreamsCount);
    return () => {
      mounted = false;
      window.removeEventListener("focus", loadDreamsCount);
    };
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    let mounted = true;
    const loadAvatar = async () => {
      if (!authReady || !isAuthenticated) return;
      try {
        const response = await avatarClient.get();
        if (mounted) setAvatar(response.avatar || null);
      } catch (error) {
        if (mounted) setAvatarStatus(error?.message || profileCopy.avatarLoadFailed);
      }
    };
    loadAvatar();
    return () => { mounted = false; };
  }, [authReady, isAuthenticated, profileCopy.avatarLoadFailed]);

  useEffect(() => () => {
    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
  }, [avatarPreviewUrl]);

  useEffect(() => {
    if (authReady && !isAuthenticated) {
      const key = readPasswordChangeKey();
      const target = key ? `/profil?password_change=${encodeURIComponent(key)}` : "/profil";
      window.location.hash = `#/zaloguj?redirect_to=${encodeURIComponent(target)}`;
    }
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    const syncMailKey = () => {
      const key = readPasswordChangeKey();
      if (!key) return;
      setMailKey(key);
      setSecurityStep("form");
      setPasswordStatus(profileCopy.linkConfirmed);
    };
    window.addEventListener("hashchange", syncMailKey);
    syncMailKey();
    return () => window.removeEventListener("hashchange", syncMailKey);
  }, [profileCopy.linkConfirmed]);

  const memberSinceLabel = useMemo(() => formatDate(user?.createdAt, lang), [user, lang]);
  if (!authReady || !isAuthenticated || !user) return null;

  const saveEdit = () => {
    const trimmed = draftName.trim();
    if (trimmed) updateProfile({ displayName: trimmed });
    setEditing(false);
  };

  const handleAvatarFile = (event) => {
    const file = event.target.files?.[0] || null;
    setAvatarStatus("");
    if (avatarPreviewUrl) {
      URL.revokeObjectURL(avatarPreviewUrl);
      setAvatarPreviewUrl("");
    }
    if (!file) {
      setAvatarFile(null);
      return;
    }
    if (!file.type?.startsWith("image/")) {
      setAvatarFile(null);
      setAvatarStatus(profileCopy.avatarNoFile);
      return;
    }
    if (file.size > 1024 * 1024) {
      setAvatarFile(null);
      setAvatarStatus(profileCopy.avatarTooLarge);
      return;
    }
    setAvatarFile(file);
    setAvatarPreviewUrl(URL.createObjectURL(file));
  };

  const uploadAvatar = async () => {
    if (avatarBusy) return;
    if (!avatarFile) {
      setAvatarStatus(profileCopy.avatarNoFile);
      return;
    }
    setAvatarBusy(true);
    setAvatarStatus(profileCopy.uploadingAvatar);
    try {
      const response = await avatarClient.upload(avatarFile);
      setAvatar(response.avatar || null);
      setAvatarFile(null);
      if (avatarPreviewUrl) {
        URL.revokeObjectURL(avatarPreviewUrl);
        setAvatarPreviewUrl("");
      }
      if (fileRef.current) fileRef.current.value = "";
      setAvatarStatus(profileCopy.avatarSaved);
    } catch (error) {
      setAvatarStatus(error?.message || profileCopy.avatarFailed);
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    if (avatarBusy) return;
    setAvatarBusy(true);
    setAvatarStatus(profileCopy.removingAvatar);
    try {
      const response = await avatarClient.remove();
      setAvatar(response.avatar || null);
      setAvatarFile(null);
      if (avatarPreviewUrl) {
        URL.revokeObjectURL(avatarPreviewUrl);
        setAvatarPreviewUrl("");
      }
      if (fileRef.current) fileRef.current.value = "";
      setAvatarStatus(profileCopy.avatarRemoved);
    } catch (error) {
      setAvatarStatus(error?.message || profileCopy.avatarFailed);
    } finally {
      setAvatarBusy(false);
    }
  };

  const resetSecurityStep = () => {
    setSecurityStep("closed");
    setMailKey("");
    setPasswordStatus("");
    setCurrentPassword("");
    setNewPassword("");
    setNewPassword2("");
  };

  const requestPasswordMail = async () => {
    if (mailBusy) return;
    setMailBusy(true);
    setPasswordStatus(profileCopy.sendingLink);
    try {
      await authClient.requestPasswordChange({ returnBase: passwordChangeReturnUrl() });
      setPasswordStatus(profileCopy.passwordMailSent);
    } catch (error) {
      setPasswordStatus(error?.message || profileCopy.linkSendError);
    } finally {
      setMailBusy(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (passwordBusy) return;
    if (!mailKey) return setPasswordStatus(profileCopy.confirmFirst);
    if (!currentPassword || !newPassword || !newPassword2) return setPasswordStatus(profileCopy.fillAll);
    if (newPassword !== newPassword2) return setPasswordStatus(profileCopy.mismatch);
    if (newPassword.length < 6) return setPasswordStatus(profileCopy.tooShort);

    setPasswordBusy(true);
    setPasswordStatus(profileCopy.changingPassword);
    try {
      const rewrapped = await vaultEngine.rewrap({ currentPassword, newPassword });
      const response = await authClient.changePassword({ currentPassword, newPassword, vault: rewrapped.vault, passwordChangeToken: mailKey });
      if (response.token) authClient.setToken(response.token);
      if (response.user) updateProfile(response.user);
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
      setMailKey("");
      setSecurityStep("closed");
      window.location.hash = "#/profil";
      setPasswordStatus(profileCopy.passwordChanged);
    } catch (error) {
      setPasswordStatus(error?.message || profileCopy.passwordChangeError);
    } finally {
      setPasswordBusy(false);
    }
  };

  const requestAccountDelete = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    setDeleteStatus(profileCopy.sendingLink);
    try {
      await authClient.requestAccountDelete();
      setDeleteStatus(profileCopy.deleteMailSent);
    } catch (error) {
      setDeleteStatus(error?.message || profileCopy.linkSendError);
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.hash = "#top";
  };

  const avatarSrc = avatarPreviewUrl || avatar?.url || "";

  return (
    <section className="mdPage" data-testid="profile-page">
      <div className="mdPage__inner mdPage__inner--wide">
        <div>
          <p className="mdPage__eyebrow">{copy.eyebrow || fallback.eyebrow}</p>
          <h1 className="mdPage__title">{copy.titlePre || fallback.titlePre} <em data-testid="profile-name">{user.displayName}</em></h1>
          <p className="mdPage__subtitle">{copy.subtitle}</p>
        </div>

        <div className="mdCard">
          <div className="mdCard__header"><p className="mdPage__eyebrow" data-ui="profile-account-eyebrow">{copy.accountTitle || fallback.accountTitle}</p></div>

          <div className="mdCard" data-ui="profile-avatar-card">
            <div className="mdRow" data-ui="profile-avatar-row">
              {avatarSrc && <img src={avatarSrc} alt="Avatar" width="96" height="96" data-ui="profile-avatar-preview" />}
              <div data-ui="profile-avatar-info">
                <p className="mdPage__eyebrow" data-ui="profile-avatar-eyebrow">{profileCopy.avatarTitle}</p>
                <p className="mdPage__subtitle" data-ui="profile-avatar-hint">{profileCopy.avatarHint}</p>
                {avatarFile && <p className="mdField__label" data-ui="profile-avatar-selected">{avatarFile.name}</p>}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarFile} data-ui="profile-avatar-file" hidden />
            <div className="mdRow" data-ui="profile-avatar-actions">
              <button type="button" className="mdBtn mdBtn--row" onClick={() => fileRef.current?.click()} disabled={avatarBusy}>{profileCopy.chooseAvatar}</button>
              <button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={uploadAvatar} disabled={avatarBusy || !avatarFile}>{avatarBusy ? profileCopy.uploadingAvatar : profileCopy.uploadAvatar}</button>
              <button type="button" className="mdBtn mdBtn--row" onClick={removeAvatar} disabled={avatarBusy || (!avatar?.custom && !avatarPreviewUrl)}>{avatarBusy ? profileCopy.removingAvatar : profileCopy.removeAvatar}</button>
            </div>
            {avatarStatus && <div className="mdAlert mdAlert--info" data-ui="profile-avatar-status" role="status">{avatarStatus}</div>}
          </div>

          <PwaInstallPanel lang={lang} />

          <div className="mdField"><span className="mdField__label">{copy.email || "Email"}</span><div className="mdField__input" data-ui="profile-readonly-field" data-testid="profile-email">{user.email}</div></div>

          <div className="mdField">
            <span className="mdField__label">{copy.displayName || fallback.displayName}</span>
            {editing ? (
              <div className="mdRow" data-ui="profile-edit-row">
                <input className="mdField__input" value={draftName} onChange={(e) => setDraftName(e.target.value)} data-testid="profile-name-input" />
                <button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={saveEdit} data-testid="profile-save">{copy.save || fallback.save}</button>
                <button type="button" className="mdBtn mdBtn--row" onClick={() => setEditing(false)} data-testid="profile-cancel">{copy.cancel || fallback.cancel}</button>
              </div>
            ) : (
              <div className="mdRow" data-ui="profile-edit-row">
                <div className="mdField__input" data-ui="profile-readonly-field">{user.displayName}</div>
                <button type="button" className="mdBtn mdBtn--row" onClick={() => { setDraftName(user.displayName || ""); setEditing(true); }} data-testid="profile-edit">{copy.editDisplayName || fallback.editDisplayName}</button>
              </div>
            )}
          </div>

          <div className="mdField"><span className="mdField__label">{copy.memberSince || fallback.memberSince}</span><div className="mdField__input" data-ui="profile-readonly-field" data-testid="profile-member-since">{memberSinceLabel}</div></div>

          <div className="mdCard" data-ui="profile-password-card">
            <div className="mdCard__header"><p className="mdPage__eyebrow" data-ui="profile-password-eyebrow">{profileCopy.security}</p></div>
            {securityStep === "closed" && <div className="mdRow" data-ui="profile-password-closed"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={() => setSecurityStep("intro")}>{profileCopy.changePassword}</button></div>}
            {securityStep === "intro" && (
              <div data-ui="profile-password-intro">
                <p className="mdPage__subtitle" data-ui="profile-password-hint">{profileCopy.passwordIntro}</p>
                <div className="mdRow" data-ui="profile-password-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={requestPasswordMail} disabled={mailBusy}>{mailBusy ? profileCopy.sending : profileCopy.sendMail}</button><button type="button" className="mdBtn mdBtn--row" onClick={resetSecurityStep}>{copy.cancel || fallback.cancel}</button></div>
              </div>
            )}
            {securityStep === "form" && (
              <form data-ui="profile-password-form" onSubmit={handlePasswordChange}>
                <div className="mdField"><span className="mdField__label">{profileCopy.currentPassword}</span><input className="mdField__input" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" /></div>
                <div className="mdField"><span className="mdField__label">{profileCopy.newPassword}</span><input className="mdField__input" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" /></div>
                <div className="mdField"><span className="mdField__label">{profileCopy.repeatPassword}</span><input className="mdField__input" type="password" value={newPassword2} onChange={(event) => setNewPassword2(event.target.value)} autoComplete="new-password" /></div>
                <p className="mdPage__subtitle" data-ui="profile-password-form-hint">{profileCopy.passwordHint}</p>
                <div className="mdRow" data-ui="profile-password-actions"><button type="submit" className="mdBtn mdBtn--primary mdBtn--row" disabled={passwordBusy}>{passwordBusy ? profileCopy.changing : profileCopy.changePassword}</button><button type="button" className="mdBtn mdBtn--row" onClick={resetSecurityStep}>{copy.cancel || fallback.cancel}</button></div>
              </form>
            )}
            {passwordStatus && <div className="mdAlert mdAlert--info" data-ui="profile-password-status" role="status">{passwordStatus}</div>}
          </div>

          <div className="mdCard" data-ui="profile-delete-card">
            <div className="mdCard__header"><p className="mdPage__eyebrow" data-ui="profile-delete-eyebrow">{profileCopy.deleteTitle}</p></div>
            {deleteStep === "closed" && <div className="mdRow" data-ui="profile-delete-closed"><button type="button" className="mdBtn mdBtn--row" onClick={() => setDeleteStep("intro")}>{profileCopy.deleteAccount}</button></div>}
            {deleteStep === "intro" && (
              <div data-ui="profile-delete-intro">
                <p className="mdPage__subtitle" data-ui="profile-delete-hint">{profileCopy.deleteHint}</p>
                <div className="mdRow" data-ui="profile-delete-actions"><button type="button" className="mdBtn mdBtn--primary mdBtn--row" onClick={requestAccountDelete} disabled={deleteBusy}>{deleteBusy ? profileCopy.sending : profileCopy.sendConfirm}</button><button type="button" className="mdBtn mdBtn--row" onClick={() => { setDeleteStep("closed"); setDeleteStatus(""); }}>{copy.cancel || fallback.cancel}</button></div>
              </div>
            )}
            {deleteStatus && <div className="mdAlert mdAlert--info" data-ui="profile-delete-status" role="status">{deleteStatus}</div>}
          </div>

          <div className="mdProfileGrid"><div className="mdStat" data-testid="profile-stat-dreams"><div className="mdStat__num">{dreamsCount}</div><div className="mdStat__label">{copy.dreamsCount || fallback.dreamsCount}</div></div><div className="mdStat"><div className="mdStat__num">—</div><div className="mdStat__label">{copy.tier || profileCopy.tierFallback}</div></div></div>

          <div className="mdRow" data-ui="profile-actions-row"><a className="mdBtn mdBtn--row" href="#/pokoik" data-testid="profile-room-link">{copy.backToRoom || fallback.backToRoom}</a><a className="mdBtn mdBtn--row" href="#/moje-sny" data-testid="profile-dreams-link">{copy.myDreams || fallback.myDreams}</a><button type="button" className="mdBtn mdBtn--row" onClick={handleLogout} data-testid="profile-logout">{copy.logout || fallback.logout}</button></div>
        </div>
      </div>
    </section>
  );
};
